import { inject, Injectable } from '@angular/core';
import { ApiCallingService, ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { map, Observable, tap } from 'rxjs';
import {
  WarrantyCategory,
  WarrantyLocation,
  warrantyTypes,
  WarrantyCraft,
  WarrantyDeficiency,
  WarrantyOption,
  LotDeficiency,
  LotDeficiencyCreateObj,
  LotOption,
  WarrantyWorkOrderStatus,
  AssignDeficiencyPayload
} from '@zayan-erp/warranty';
import { WarrantyTechnicianUser } from '@zayan-erp/administration';
import { SetupUser } from '@zayan-erp/shared';
import { WorkOrder } from '@zayan-erp/warranty';
import { ResponseUtilityService } from '@zayan-erp/shared';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class WarrantyService {
  // #http = inject(ApiCallingService);
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  #responseHandler = inject(ResponseUtilityService);
  #toastr = inject(ToastrService);
  createWarrantyLocation(location: Omit<WarrantyLocation, 'id'>): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/locations`,
      { showLoader: true, showMessage: true, message: 'Warranty Location created successfully' },
      location
    );
  }

  readWarrantyLocations(showActiveOnly: boolean = true): Observable<WarrantyLocation[]> {
    return this.#http.getData(`${this.baseUrl}/locations?includingInActive=${!showActiveOnly}`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateWarrantyLocation(location: WarrantyLocation): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/locations/${location.id}`, location, {
      showLoader: true,
      showMessage: true,
      message: 'Warranty Location updated successfully'
    });
  }

  deleteWarrantyLocation(id: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/locations/${id}`, {
      showLoader: true,
      showMessage: true,
      message: 'Warranty Location deleted successfully'
    });
  }
  toggleWarrantyLocation(id: string): Observable<unknown> {
    return this.#http.patchData(`${this.baseUrl}/locations/${id}`, {}, { showLoader: true, showMessage: false });
  }

  readWarrantyCategories(showActiveOnly: boolean): Observable<WarrantyCategory[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/categories?includingInActive=${!showActiveOnly}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readWarrantyUsers(): Observable<SetupUser[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/users`, { showLoader: true, showMessage: false });
  }
  readWarrantyData(): Observable<warrantyTypes[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/types?includingInActive=true`, {
      showLoader: true,
      showMessage: false
    });
  }
  readWarrantyRoles(): Observable<any[]> {
    return this.#http.getData(`${this.baseUrl}/modules`, { showLoader: true, showMessage: false });
  }

  updateWarrantyUser(obj: any, id: string): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/warranty/users/${id}`,
      { showLoader: true, showMessage: true, message: 'Warranty user updated successfully' },
      obj
    );
  }

  toggleWarrantyCategory(id: string): Observable<unknown> {
    return this.#http.patchData(
      `${this.baseUrl}/warranty/categories/${id}`,
      {},
      { showLoader: true, showMessage: false }
    );
  }

  toggleWarrantyCategoryLocation(categoryId: string, locationId: string, isActive: boolean): Observable<unknown> {
    return this.#http.patchData(
      `${this.baseUrl}/warranty/categories/${categoryId}/locations/${locationId}?isActive=${isActive}`,
      {},
      { showLoader: true, showMessage: false }
    );
  }

  readWarrantyCategoryLocations(categoryId: string): Observable<WarrantyCategory> {
    return this.#http.getData(`${this.baseUrl}/warranty/categories/${categoryId}/locations`, {
      showLoader: true,
      showMessage: false
    });
  }

  createWarrantyCategory(
    category: Pick<WarrantyCategory, 'categoryCode' | 'categoryDescription'> & {
      locationIds: string[];
    }
  ): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/warranty/categories/with-locations`,
      { showLoader: true, showMessage: true, message: 'Warranty Category created successfully' },
      category
    );
  }
  updateWarrantyCategory(
    category: Pick<WarrantyCategory, 'categoryDescription'> & {
      locationIds: string[];
    },
    categoryId: string
  ): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/warranty/categories/${categoryId}/locations`,
      { showLoader: true, showMessage: true, message: 'Warranty Category updated successfully' },
      category
    );
  }

  readWarrantyDeficiencies(showActiveOnly: boolean = true): Observable<WarrantyDeficiency[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/deficiencies?includingInActive=${!showActiveOnly}`, {
      showLoader: true,
      showMessage: false
    });
  }
  createWarrantyDeficiency(
    deficiency: Pick<WarrantyDeficiency, 'deficiencyCode' | 'deficiencyDescription' | 'craftId' | 'categoryId'>
  ): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/warranty/deficiencies`,
      { showLoader: true, showMessage: true, message: 'Warranty Deficiency created successfully' },
      deficiency
    );
  }

  updateWarrantyDeficiency(
    deficiency: Pick<WarrantyDeficiency, 'deficiencyDescription' | 'craftId' | 'categoryId'>,
    deficiencyId: string
  ): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/warranty/deficiencies/${deficiencyId}`, deficiency, {
      showLoader: true,
      showMessage: true,
      message: 'Warranty Deficiency updated successfully'
    });
  }

  toggleWarrantyDeficiency(id: string): Observable<unknown> {
    return this.#http.patchData(
      `${this.baseUrl}/warranty/deficiencies/${id}`,
      {},
      { showLoader: true, showMessage: false }
    );
  }

  readWarrantyCrafts(includeInactive: boolean = false): Observable<WarrantyCraft[]> {
    return this.#http.getData(`${this.baseUrl}/crafts?includingInActive=${includeInactive}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readWarrantyOptions(): Observable<WarrantyOption[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/options`, { showLoader: true, showMessage: false });
  }

  createWarrantyOptions(
    options: Omit<WarrantyOption, 'lotNo' | 'id' | 'projectCode'> & {
      lotId: string;
    }
  ): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/warranty/options`,
      { showLoader: true, showMessage: true, message: 'Warranty Options created successfully' },
      options
    );
  }

  updateWarrantyOptions(
    options: Omit<WarrantyOption, 'lotNo' | 'id' | 'projectCode'> & {
      lotId: string;
    },
    optionId: string
  ): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/warranty/options/${optionId}`, options, {
      showLoader: true,
      showMessage: true,
      message: 'Warranty Options updated successfully'
    });
  }

  deleteWarrantyOptions(optionId: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/warranty/options/${optionId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Warranty Options deleted successfully'
    });
  }

  readWarrantyTypes(): Observable<warrantyTypes[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/types`, { showLoader: true, showMessage: false });
  }

  readWarrantyTechnicians(): Observable<WarrantyTechnicianUser[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/users/technicians`, { showLoader: true, showMessage: false });
  }

  createLotDeficiency(lotDeficiency: LotDeficiencyCreateObj): Observable<unknown> {
    if (lotDeficiency.scheduledDate) {
      lotDeficiency.scheduledDate = new Date(lotDeficiency.scheduledDate).toISOString();
    }

    return this.#http.postData(
      `${this.baseUrl}/lots/${lotDeficiency.lotId}/deficiencies`,
      { showLoader: true, showMessage: true, message: 'Work Order Created Successfully' },
      lotDeficiency
    );
  }
  assignLotDeficiencies(payload: AssignDeficiencyPayload): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/lots/deficiencies/assignments`,
      { showLoader: true, showMessage: true, message: 'Deficiencies Assigned Successfully' },
      payload
    );
  }
  rescheduleLotDeficiency(deficiencyId: string, data: any): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/lots/deficiencies/${deficiencyId}/reschedule`, data, {
      showLoader: true,
      showMessage: true,
      message: 'Work Order Rescheduled Successfully'
    });
  }

  updateLotDeficiency(lotDeficiency: LotDeficiencyCreateObj, deficiencyId: string | null): Observable<unknown> {
    if (lotDeficiency.scheduledDate) {
      lotDeficiency.scheduledDate = new Date(lotDeficiency.scheduledDate).toISOString();
    }
    return this.#http.putData(
      `${this.baseUrl}/lots/${lotDeficiency.lotId}/deficiencies/${deficiencyId}`,
      lotDeficiency,
      { showLoader: true, showMessage: true, message: 'Work Order Updated Successfully' }
    );
  }

  readDeficiencies(
    projectId: string | null,
    lotId: string | null,
    warrantyTypeId: string | null,
    locationId: string | null, // Added to match WarrantySearchComponent's call
    assignedToTechnician: string | null,
    status: string | null,
    searchTerm: string | null,
    deficiencyCode: string | null, // Changed to match capitalization
    scheduleFromDate: string | null,
    scheduleToDate: string | null,
    assignedToTrade: string | null, // Moved after assignedToTechnician for consistency
    pageNumber: number | null,
    pageSize: number | null,
    sortField: string | null, // Added to match WarrantySearchComponent
    createdFromDate: string | null, // Added to match WarrantySearchComponent
    createdToDate: string | null, // Changed Source to lowercase for consistency
    oUnitCodeId: string | null = null
  ): Observable<LotDeficiency[]> {
    const queryParams = new URLSearchParams();

    // Add non-null parameters to the query string
    if (projectId) queryParams.set('projectId', projectId);
    if (lotId) queryParams.set('lotId', lotId);
    if (warrantyTypeId) queryParams.set('warrantyTypeId', warrantyTypeId);
    if (locationId) queryParams.set('locationId', locationId);
    if (assignedToTechnician) queryParams.set('assignedToTechnician', assignedToTechnician);
    if (assignedToTrade) queryParams.set('assignedToTrade', assignedToTrade);
    if (status) queryParams.set('status', status);
    if (searchTerm) queryParams.set('searchTerm', searchTerm);
    if (deficiencyCode) queryParams.set('deficiencyCode', deficiencyCode);
    if (scheduleFromDate) queryParams.set('scheduleFromDate', scheduleFromDate);
    if (scheduleToDate) queryParams.set('scheduleToDate', scheduleToDate);
    if (pageNumber !== null) queryParams.set('pageNumber', pageNumber.toString());
    if (pageSize !== null) queryParams.set('pageSize', pageSize.toString());
    if (sortField) queryParams.set('sortField', sortField);
    if (createdFromDate) queryParams.set('createdFromDate', createdFromDate);
    if (createdToDate) queryParams.set('createdToDate', createdToDate);
    if (oUnitCodeId) queryParams.set('oUnitCodeId', oUnitCodeId);

    // Note: isValidDateRange, createdFromDate, and createdToDate were not used in WarrantySearchComponent's call
    // If needed, uncomment and add logic:
    // if (isValidDateRange !== null) queryParams.set('isValidDateRange', isValidDateRange.toString());
    // if (createdFromDate) queryParams.set('createdFromDate', createdFromDate);
    // if (createdToDate) queryParams.set('createdToDate', createdToDate);

    const url = `deficiencies?${queryParams.toString()}`;

    return this.#http.getData(`${this.baseUrl}/lots${url}`, { showLoader: true, showMessage: false });
  }

  readLotDeficiency(deficiencyId: string): Observable<LotDeficiency> {
    return this.#http.getData(`${this.baseUrl}/lots/deficiencies/${deficiencyId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  generatePDF(lotDeficiencyIds: string[]): Observable<void> {
    const body = {
      lotDeficiencyIds: lotDeficiencyIds
    };
    return this.#http.downloadFile(
      `${this.baseUrl}/lots/deficiencies/report`,
      body,
      { showLoader: true, showMessage: true, message: 'PDF generated successfully' },
      `deficiencyReport_${+new Date()}.pdf`
    );
  }

  getLotOptions(lotId: string): Observable<LotOption[]> {
    return this.#http.getData(`${this.baseUrl}/warranty/options?lotId=${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  completeLotDeficiency(deficiencyIds: string[], isCompleted?: boolean | null): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/lots/deficiencies/is-completed`,
      { lotDeficiencyIds: deficiencyIds, isCompleted: isCompleted },
      { showLoader: true, showMessage: true, message: 'Work Order Completed Successfully' }
    );
  }

  updateLotDeficiencyForTechnician(
    lotId: string,
    lotDeficiencyId: string,
    status: WarrantyWorkOrderStatus | null,
    keyImages: any[] = [],
    detailDescription?: string
  ): Observable<unknown> {
    const payload = {
      status: status,
      keyImages: keyImages,
      DetailDescription: detailDescription
    };

    return this.#http.putData(
      `${this.baseUrl}/lots/${lotId}/deficiencies/${lotDeficiencyId}/technician-update`,
      payload,
      { showLoader: true, showMessage: true }
    );
  }

  markAllAsCompleted(deficiencyIds: string[]): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/lots/deficiencies/is-completed`,
      { lotDeficiencyIds: deficiencyIds, isCompleted: true },
      { showLoader: true, showMessage: true }
    );
  }

  bulkAssignLotDeficiencies(
    deficiencyIds: string[],
    assignedToTechnician?: string | null,
    assignedToTrade?: string | null,
    enableInstantEmail?: boolean,
    scheduledDate?: string | null
  ): Observable<unknown> {
    const payload = {
      deficiencyIds,
      assignedToTechnician,
      assignedToTrade,
      enableInstantEmail,
      scheduledDate
    };

    return this.#http.putData(`${this.baseUrl}/lots/deficiencies/bulk-assign`, payload, {
      showLoader: true,
      showMessage: true,
      message: 'Work orders assigned successfully'
    });
  }

  createBulkWorkOrder(bulkWorkOrder: any[]): Observable<unknown> {
    // Ensure each DTO has the required structure
    // const formattedDto = {
    //   lotId: bulkWorkOrder.lotId,
    //   warrantyTypeId: bulkWorkOrder.warrantyTypeId,
    //   status: bulkWorkOrder.status || "Not Started",
    //   keyImages: bulkWorkOrder.keyImages || [],
    //   enableInstantEmail: bulkWorkOrder.enableInstantEmail || false,
    //   mappedDeficiencies: (bulkWorkOrder.mappedDeficiencies || []).map((def: any) => ({
    //     locationId: def.locationId,
    //     detailDescription: def.detailDescription || "",
    //     assignedToTrade: def.assignedToTrade || null,
    //     assignedToTechnician: def.assignedToTechnician || null,
    //     scheduledDate: def.scheduledDate || null,
    //     deficiencyId: def.deficiencyId
    //   }))
    // };
    // const formattedDto = bulkWorkOrder.map((workOrder: any) => {
    //   return {
    //     lotId: workOrder.lotId,
    //     warrantyTypeId: workOrder.warrantyTypeId,
    //     status: workOrder.status || "Not Started",
    //     keyImages: workOrder.keyImages || [],
    //     enableInstantEmail: workOrder.enableInstantEmail || false,
    //     mappedDeficiencies: workOrder.mappedDeficiencies || []
    //   };
    // });

    // Make the API call
    return this.#http.postData(
      `${this.baseUrl}/lots/deficiencies/bulk`,
      { showLoader: true, showMessage: true, message: 'Work orders assigned successfully' },
      bulkWorkOrder
    );
  }

  validateBulkDeficiencies(bulkWorkOrder: WorkOrder[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/lots/deficiencies/upload-xml`,
      { showLoader: true, showMessage: true, message: 'Work orders assigned successfully' },
      bulkWorkOrder
    );
  }

  //warranty reports start
  readReports(request: any, url: string): Observable<any[]> {
    const queryParams = new URLSearchParams();
    if (request.companyId) queryParams.set('companyId', request.companyId);
    if (request.projectId) queryParams.set('projectId', request.projectId);
    if (request.lotId) queryParams.set('lotId', request.lotId);
    if (request.warrantyTypeId) queryParams.set('warrantyTypeId', request.warrantyTypeId);
    if (request.tradeId) queryParams.set('TradeId', request.tradeId);
    if (request.technicianId) queryParams.set('TechnicianId', request.technicianId);
    if (request.deficiencyId) queryParams.set('deficiencyId', request.deficiencyId);
    if (request.status) queryParams.set('status', request.status);
    if (request.createdDateFrom) queryParams.set('createdDateFrom', request.createdDateFrom);
    if (request.createdDateTo) queryParams.set('createdDateTo', request.createdDateTo);
    if (request.isEscalated) queryParams.set('isEscalated', request.isEscalated);
    if (request.locationId) queryParams.set('locationId', request.locationId);
    if (request.categoryId) queryParams.set('categoryId', request.categoryId);
    if (request.oUnitCodeId) queryParams.set('oUnitCodeId', request.oUnitCodeId);
    if (request.createdDateFrom) queryParams.set('scheduledDateFrom', request.createdDateFrom);
    if (request.createdDateTo) queryParams.set('scheduledDateTo', request.createdDateTo);
    return this.#http
      .getData(`${this.baseUrl}/warranty/${url}?${queryParams.toString()}`, { showLoader: true, showMessage: false })
      .pipe(
        map(data => {
          if (!data.success) throw data;
          return this.#responseHandler.handleResponse<any[]>(data, undefined, false) as any[];
          // return data.data;
        })
      );
  }

  exportReports(request: any, url: string): Observable<void> {
    const queryParams = new URLSearchParams();

    Object.entries(request).forEach(([key, value]) => {
      // Only add query param if value is not null/undefined
      if (value !== null && value !== undefined) {
        queryParams.set(key, value.toString());
      }
    });

    return this.#http.downloadReportFile(
      `${this.baseUrl}/warranty/${url}?${queryParams.toString()}`,
      {},
      { showLoader: true, showMessage: true, message: 'PDF generated successfully' },
      'warrantyReport.pdf'
    );
  }

  //warranty reports end
}
